import { createContext, useReducer, useContext, ReactNode } from 'react';

enum DialogActionType {
  OPEN_DIALOG = 'OPEN_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
}

export enum DatasetDialogType {
  VIEW_DATASET_DETAIL = 'datasetDetail',
}

type DatasetModalAction =
  | {
      type: DialogActionType.OPEN_DIALOG;
      payload: {
        dialogType: DatasetDialogType;
        itemId: string | null;
      };
    }
  | { type: DialogActionType.CLOSE_DIALOG };

type DatasetDetailModalState = {
  activeDialog?: DatasetDialogType | null;
  itemId?: string | null;
};

const initialProjectState: DatasetDetailModalState = {
  activeDialog: null,
  itemId: null,
};

const dialogReducer = (
  state: DatasetDetailModalState,
  action: DatasetModalAction
): DatasetDetailModalState => {
  switch (action.type) {
    case DialogActionType.OPEN_DIALOG:
      return {
        ...state,
        activeDialog: action.payload.dialogType,
        itemId: action.payload.itemId,
      };
    case DialogActionType.CLOSE_DIALOG:
      return { ...state, activeDialog: null, itemId: null };
    default:
      return state;
  }
};

const DatasetDetailModalContext = createContext<{
  state: DatasetDetailModalState;
  openDialog: (dialogType: DatasetDialogType, itemId?: string | null) => void;
  closeDialog: () => void;
}>({
  state: initialProjectState,
  openDialog: () => null,
  closeDialog: () => null,
});

const DatasetModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dialogReducer, initialProjectState);

  const openDialog = (dialogType: DatasetDialogType, itemId: string | null = null) => {
    dispatch({ type: DialogActionType.OPEN_DIALOG, payload: { dialogType, itemId } });
  };

  const closeDialog = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  return (
    <DatasetDetailModalContext.Provider value={{ state, openDialog, closeDialog }}>
      {children}
    </DatasetDetailModalContext.Provider>
  );
};

const useDatasetModalState = () => {
  const { state } = useContext(DatasetDetailModalContext);
  return state;
};

const useDatasetModalDispatch = () => {
  const { openDialog, closeDialog } = useContext(DatasetDetailModalContext);
  return { openDialog, closeDialog };
};

const useDatasetModalContext = () => {
  const state = useDatasetModalState();
  const { openDialog, closeDialog } = useDatasetModalDispatch();
  return { ...state, openDialog, closeDialog };
};

export { DatasetModalProvider, useDatasetModalContext };
